<script lang="ts">
  import { onMount } from "svelte";
  import { apiFetch, ApiError } from "$lib/api";
  import SignupPlan from "./SignupPlan.svelte";
  import CreatableMultiSelect, { type MultiSelectValue } from "./CreatableMultiSelect.svelte";
  import type { Plan } from "$lib/pricing";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { loadStripe, type Stripe, type StripeCardElement } from "@stripe/stripe-js";

  interface Props {
    stripeKey?: string;
    plans?: Plan[];
  }

  let { stripeKey = "", plans = [] }: Props = $props();

  type Option = { id: number; name: string };
  type CreateResponse = { status: string; card_token?: string; reason?: string };

  // ----- form state ------------------------------------------------------
  let firstName = $state("");
  let lastName = $state("");
  let email = $state("");
  let phone = $state("");
  let password = $state("");
  let termsAccepted = $state(false);

  let companyName = $state("");
  let companyWebsite = $state("");
  let mainOfficePhone = $state("");
  let tenantId = $state("");

  let verticals = $state<Option[]>([]);
  let metros = $state<Option[]>([]);
  let selectedVerticals = $state<MultiSelectValue[]>([]);
  let selectedMetros = $state<MultiSelectValue[]>([]);

  // ----- flow state ------------------------------------------------------
  // "form" (steps 1–2) → "card" (step 3) → "done" | "existing" | "review"
  let wizardStep = $state(1);
  let loading = $state(false);
  let errorMessage = $state("");
  let outcome = $state<"" | "existing" | "review" | "done">("");
  let reviewReason = $state("");

  // Live email check (mirrors the onboarding state machine's email branch).
  let emailTaken = $state(false);
  let emailChecking = $state(false);
  let checkedEmail = $state("");

  // "I need help" request for an existing-account visitor.
  let helpLoading = $state(false);
  let helpRequested = $state(false);
  let helpError = $state(false);

  let cardToken = "";
  let stripe: Stripe | null = null;
  let cardElement: StripeCardElement | null = null;
  let cardEl = $state<HTMLDivElement | null>(null);
  let cardMounted = $state(false);
  let cardError = $state("");

  const stepLabels = ["You", "Company", "Payment"];

  // Plan selection — pre-pick the server-flagged default (falls back to the first
  // offered plan). With one plan this stays invisible; with several it's a radio group.
  let selectedPlanId = $state(plans.find((p) => p.default)?.id ?? plans[0]?.id ?? "");
  const hasChoice = plans.length > 1;

  const tenantIdValid = $derived(/^\d+$/.test(tenantId.trim()));
  const emailValid = $derived(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
  // Must match GetStartedController::PASSWORD_RULE (server-side is the real gate).
  const passwordValid = $derived(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(password)
  );
  const step1Ready = $derived(
    Boolean(
      firstName.trim() &&
        lastName.trim() &&
        emailValid &&
        phone.trim() &&
        passwordValid &&
        termsAccepted
    )
  );
  const planReady = $derived(!hasChoice || Boolean(selectedPlanId));
  const step2Ready = $derived(Boolean(companyName && tenantIdValid) && planReady);

  // Once they try to advance, highlight any required field still missing/invalid.
  let attemptedStep1 = $state(false);
  let attemptedStep2 = $state(false);
  const firstInvalid = $derived(attemptedStep1 && !firstName.trim());
  const lastInvalid = $derived(attemptedStep1 && !lastName.trim());
  const emailInvalid = $derived(attemptedStep1 && !emailValid);
  const phoneInvalid = $derived(attemptedStep1 && !phone.trim());
  const passwordInvalid = $derived(attemptedStep1 && !passwordValid);
  const termsInvalid = $derived(attemptedStep1 && !termsAccepted);
  const companyInvalid = $derived(attemptedStep2 && !companyName.trim());
  const tenantInvalid = $derived(attemptedStep2 && !tenantIdValid);
  // The nudge stays visible only while something is still missing, then clears itself.
  const showStep1Prompt = $derived(attemptedStep1 && !step1Ready);
  const showStep2Prompt = $derived(attemptedStep2 && !step2Ready);

  onMount(async () => {
    // Option lists are informational only — they never block the form if they fail.
    // Plans are rendered server-side and passed in as a prop (no public catalog fetch).
    try {
      verticals = await apiFetch<Option[]>("/api/public/business_verticals");
    } catch {
      /* optional */
    }
    try {
      metros = await apiFetch<Option[]>("/api/public/metropolitans");
    } catch {
      /* optional */
    }
  });

  // As-you-type US phone formatting: (415) 555-2671. Our audience is US-only, so we
  // skip a heavier international component (and its confusing country dropdown).
  function formatUsPhone(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function onPhoneInput(e: Event) {
    phone = formatUsPhone((e.currentTarget as HTMLInputElement).value);
  }

  function onMainOfficePhoneInput(e: Event) {
    mainOfficePhone = formatUsPhone((e.currentTarget as HTMLInputElement).value);
  }

  // ServiceTitan tenant ids are numeric-only — strip anything else as they type.
  function onTenantInput(e: Event) {
    tenantId = (e.currentTarget as HTMLInputElement).value.replace(/\D/g, "");
  }

  // Friendly URL entry: add a scheme on blur so "chilla-u-house.com" is stored as a
  // real URL. Left blank stays blank (the field is optional).
  function normalizeWebsite() {
    const v = companyWebsite.trim();
    companyWebsite = v && !/^https?:\/\//i.test(v) ? `https://${v}` : v;
  }

  // Selected options plus any free-text entry, as a uniform array of {name, id?} hashes for
  // a human to review later — a free-text entry simply has no id.
  // (Selection now flows straight from CreatableMultiSelect.)

  // On email blur, ask the server whether an owner already exists so we can offer a
  // log-in link right away. A failed check never blocks the form.
  async function checkEmail() {
    const value = email.trim().toLowerCase();
    // Only ask the DB about a well-formed address we haven't checked yet.
    if (!value || value === checkedEmail || !emailValid) return;
    checkedEmail = value;
    emailChecking = true;
    try {
      const res = await apiFetch<{ taken: boolean }>(
        `/api/public/email_availability?email=${encodeURIComponent(value)}`
      );
      emailTaken = res.taken;
    } catch {
      emailTaken = false;
    } finally {
      emailChecking = false;
    }
  }

  function onEmailInput() {
    // Clear a stale "already taken" notice as soon as the address changes.
    if (email.trim().toLowerCase() !== checkedEmail) emailTaken = false;
    helpRequested = false;
  }

  // Existing-account visitor asks us to reach out. We notify the onboarding team; the
  // visitor only ever sees a confirmation, never the internal address.
  async function requestHelp() {
    if (helpLoading || helpRequested) return;
    helpLoading = true;
    try {
      await apiFetch("/get-started-servicetitan/help", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
        }),
      });
      helpRequested = true;
    } catch {
      helpError = true;
    } finally {
      helpLoading = false;
    }
  }

  function goToStep2() {
    attemptedStep1 = true;
    if (!step1Ready) return;
    errorMessage = "";
    wizardStep = 2;
  }

  function backToStep1() {
    errorMessage = "";
    wizardStep = 1;
  }

  async function submitInfo() {
    attemptedStep2 = true;
    if (!step2Ready) return;
    loading = true;
    errorMessage = "";

    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      password,
      company_name: companyName,
      website: companyWebsite.trim(),
      main_office_phone: mainOfficePhone.trim(),
      tenant_id: tenantId.trim(),
      terms_of_service: termsAccepted,
      plan_id: selectedPlanId,
      intake_data: {
        verticals: selectedVerticals,
        metros: selectedMetros,
      },
    };

    try {
      const res = await apiFetch<CreateResponse>("/get-started-servicetitan", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (res.status === "created" && res.card_token) {
        cardToken = res.card_token;
        wizardStep = 3;
      } else if (res.status === "existing_account") {
        outcome = "existing";
      } else {
        reviewReason = res.reason ?? "";
        outcome = "review";
      }
    } catch (e) {
      if (e instanceof ApiError && e.body?.error === "invalid_tenant_id") {
        errorMessage = "That ServiceTitan ID doesn't look right. It should be numbers only.";
      } else if (e instanceof ApiError && e.body?.error === "weak_password") {
        errorMessage = "Your password needs at least 8 characters, an upper- and lower-case letter, a number, and a symbol.";
      } else if (e instanceof ApiError && typeof e.body?.error === "string") {
        errorMessage = e.body.error as string;
      } else {
        errorMessage = "Something went wrong. Please try again.";
      }
    } finally {
      loading = false;
    }
  }

  // Set up the Stripe card field the moment we land on the card step.
  $effect(() => {
    if (wizardStep === 3 && cardEl && !cardMounted) {
      void initCard();
    }
  });

  async function initCard() {
    cardError = "";
    try {
      stripe = await loadStripe(stripeKey);
      if (!stripe || !cardEl) {
        cardError = "We couldn't load the secure card form. Please refresh and try again.";
        return;
      }
      const elements = stripe.elements();
      cardElement = elements.create("card", {
        style: {
          base: {
            fontFamily:
              'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: "16px",
            fontSmoothing: "antialiased",
          },
        },
      });
      cardElement.mount(cardEl);
      cardElement.on("change", (ev) => {
        cardError = ev.error?.message ?? "";
      });
      cardMounted = true;
    } catch {
      cardError = "We couldn't load the secure card form. Please refresh and try again.";
    }
  }

  async function submitCard() {
    if (!stripe || !cardElement) return;
    loading = true;
    cardError = "";

    try {
      const { client_secret } = await apiFetch<{ client_secret: string }>(
        "/get-started-servicetitan/setup_intent",
        { method: "POST", body: JSON.stringify({ card_token: cardToken }) }
      );

      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: `${firstName} ${lastName}`.trim(), email },
        },
      });

      if (result.error) {
        cardError = result.error.message ?? "We couldn't save your card. Please check the details.";
        return;
      }

      const paymentMethodId = result.setupIntent?.payment_method;
      if (typeof paymentMethodId !== "string") {
        cardError = "We couldn't save your card. Please try again.";
        return;
      }

      await apiFetch("/get-started-servicetitan/payment_method", {
        method: "POST",
        body: JSON.stringify({ card_token: cardToken, payment_method_id: paymentMethodId }),
      });

      outcome = "done";
    } catch (e) {
      if (e instanceof ApiError && typeof e.body?.error === "string") {
        cardError = e.body.error as string;
      } else {
        cardError = "We couldn't save your card. Please try again.";
      }
    } finally {
      loading = false;
    }
  }
</script>

{#snippet requiredMark()}<span class="align-middle text-base leading-none text-destructive" title="Required">*</span>{/snippet}

<div class="min-h-screen bg-background text-foreground px-4 py-10 text-base sm:px-6 lg:px-8">
  <div class="mx-auto w-full max-w-3xl space-y-8">
    <div class="flex flex-col items-center gap-2 text-center">
      <img src="/DT-logo.jpg" alt="DataTurk.ai" class="size-12 rounded-lg object-contain" />
      <span class="font-semibold text-xl tracking-wide" style="font-family: 'Sora', sans-serif;">DataTurk.ai</span>
    </div>

    {#if outcome === "done"}
      <div class="space-y-4 rounded-xl border border-border bg-card p-6 text-center shadow-sm sm:p-8">
        <h1 class="text-2xl font-semibold">You're all set!</h1>
        <p class="leading-relaxed">
          Check your email and click the link to confirm your address. After that, we'll
          start setting up your account and pulling in your ServiceTitan data.
        </p>
        <Button href="/login" class="min-h-11">Go to login</Button>
      </div>
    {:else if outcome === "existing"}
      <div class="space-y-4 rounded-xl border border-border bg-card p-6 text-center shadow-sm sm:p-8">
        <h1 class="text-2xl font-semibold">You already have an account</h1>
        <p class="leading-relaxed">
          It looks like that email is already signed up. Log in and you can finish
          setting up your plan from inside the app. We didn't charge you anything.
        </p>
        <Button href="/login" class="min-h-11">Log in</Button>
      </div>
    {:else if outcome === "review"}
      <div class="space-y-4 rounded-xl border border-border bg-card p-6 text-center shadow-sm sm:p-8">
        <h1 class="text-2xl font-semibold">We're checking a few things</h1>
        <p class="leading-relaxed">
          Thanks for signing up! Our team needs to take a quick look before we finish
          setting up your account. We'll email you soon. Nothing has been charged.
        </p>
      </div>
    {:else}
      {#if plans.length > 0 && wizardStep < 3}
        <div class="space-y-3">
          <div class="space-y-1 text-center">
            <h1 class="text-3xl font-bold tracking-tight">Get started with your ServiceTitan data</h1>
            <p class="text-base text-muted-foreground">
              {hasChoice ? "Choose your plan" : "Here's what your plan includes."}
            </p>
          </div>
          <div
            class="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(15rem,100%),1fr))]"
            role={hasChoice ? "radiogroup" : undefined}
            aria-label={hasChoice ? "Choose your plan" : undefined}
          >
            {#each plans as plan (plan.id)}
              <SignupPlan
                {plan}
                selectable={hasChoice}
                selected={selectedPlanId === plan.id}
                onselect={() => (selectedPlanId = plan.id)}
              />
            {/each}
          </div>
        </div>
      {/if}

      <div class="space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8">
        <div class="space-y-2" aria-label="Progress: step {wizardStep} of {stepLabels.length}">
          <div class="flex gap-1.5">
            {#each stepLabels as _label, i}
              <div
                class="h-1.5 flex-1 rounded-full {i < wizardStep ? 'bg-primary' : 'bg-muted'}"
              ></div>
            {/each}
          </div>
          <div class="flex text-xs">
            {#each stepLabels as label, i}
              <span
                class="flex-1 {i === 0 ? 'text-left' : i === stepLabels.length - 1 ? 'text-right' : 'text-center'} {i + 1 === wizardStep ? 'font-semibold text-foreground' : 'text-muted-foreground'}"
              >{label}</span>
            {/each}
          </div>
        </div>

        {#if wizardStep === 1}
          <div class="space-y-1">
            <h2 class="text-2xl font-semibold">Owner's info</h2>
            <p class="text-sm text-muted-foreground">
              DataTurk is built for the business owner. To start, we suggest the owner signs
              up. You can share access with a coach or senior staff later.
            </p>
            <p class="text-xs text-muted-foreground">
              Fields marked {@render requiredMark()} are required.
            </p>
          </div>

          <div class="space-y-4">
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="space-y-2">
                <Label for="gs-first-name">First name {@render requiredMark()}</Label>
                <Input id="gs-first-name" bind:value={firstName} autocomplete="given-name" aria-invalid={firstInvalid} class="min-h-11" />
              </div>
              <div class="space-y-2">
                <Label for="gs-last-name">Last name {@render requiredMark()}</Label>
                <Input id="gs-last-name" bind:value={lastName} autocomplete="family-name" aria-invalid={lastInvalid} class="min-h-11" />
              </div>
            </div>
            <div class="space-y-2">
              <Label for="gs-email">Email {@render requiredMark()}</Label>
              <Input
                id="gs-email"
                type="email"
                bind:value={email}
                autocomplete="email"
                aria-invalid={emailInvalid}
                placeholder="Example: you@company.com"
                class="min-h-11 placeholder:italic"
                oninput={onEmailInput}
                onblur={checkEmail}
              />
              <p class="text-xs text-muted-foreground">
                We use your email to tell you about next steps, and each month when your
                UpScore is generated. Your email is used to sign in to our app.
              </p>
              {#if email && !emailValid}
                <p class="text-xs text-destructive">Please enter a valid email, like you@company.com.</p>
              {/if}
              {#if emailTaken}
                <div class="rounded-lg border border-amber-400/60 bg-amber-50 p-4 text-sm dark:border-amber-600/40 dark:bg-amber-950/20">
                  <p class="text-base font-semibold text-amber-900 dark:text-amber-200">Looks like you already have an account.</p>
                  {#if helpRequested}
                    <p class="mt-1 text-amber-900/80 dark:text-amber-200/80">
                      Thanks — someone from our team will reach out to you soon.
                    </p>
                  {:else}
                    <p class="mt-1 text-amber-900/80 dark:text-amber-200/80">
                      <a href="/login" class="font-medium text-primary underline underline-offset-4">Log in</a>
                      to finish setting up your plan, or
                      <button
                        type="button"
                        onclick={requestHelp}
                        disabled={helpLoading}
                        class="font-medium text-primary underline underline-offset-4 disabled:opacity-50"
                      >{helpLoading ? "asking…" : "ask us to help"}</button>
                      if you're stuck.
                    </p>
                    {#if helpError}
                      <p class="mt-1 text-destructive">We couldn't send that just now. Please try again.</p>
                    {/if}
                  {/if}
                </div>
              {/if}
            </div>
            <div class="space-y-2">
              <Label for="gs-password">Password {@render requiredMark()}</Label>
              <Input id="gs-password" type="password" bind:value={password} autocomplete="new-password" aria-invalid={passwordInvalid} class="min-h-11" />
              <p class="text-xs text-muted-foreground">
                At least 8 characters, with an upper- and lower-case letter, a number, and a
                symbol (like ! or #).
              </p>
              {#if password && !passwordValid}
                <p class="text-xs text-destructive">Please meet all the password rules above.</p>
              {/if}
            </div>
            <div class="space-y-2">
              <Label for="gs-phone">Phone {@render requiredMark()}</Label>
              <Input id="gs-phone" type="tel" inputmode="tel" bind:value={phone} autocomplete="tel" aria-invalid={phoneInvalid} placeholder="Example: (555) 123-4567" class="min-h-11 placeholder:italic" oninput={onPhoneInput} />
              <p class="text-xs text-muted-foreground">
                We only call if there's a problem getting email to you, or if you ask us to.
                We never use your number for marketing.
              </p>
            </div>
            <label class="mt-2 flex items-start gap-3 text-sm">
              <input type="checkbox" bind:checked={termsAccepted} class="mt-1 size-5 {termsInvalid ? 'rounded ring-2 ring-destructive' : ''}" />
              <span>
                I agree to the
                <a href="/terms" target="_blank" class="font-medium text-primary underline underline-offset-4">Terms of Service</a>
                and
                <a href="/privacy" target="_blank" class="font-medium text-primary underline underline-offset-4">Privacy Policy</a>.
                {@render requiredMark()}
              </span>
            </label>
            {#if termsInvalid}
              <p class="text-xs text-destructive">Please agree to the Terms and Privacy Policy to continue.</p>
            {/if}
          </div>

          {#if errorMessage}
            <p class="text-sm text-destructive">{errorMessage}</p>
          {/if}

          {#if showStep1Prompt}
            <p class="text-sm text-destructive">Please fill in the highlighted fields.</p>
          {/if}

          <Button class="w-full min-h-11" onclick={goToStep2}>Next: Your Company</Button>
        {:else if wizardStep === 2}
          <div class="space-y-1">
            <h2 class="text-2xl font-semibold">About your company</h2>
            <p class="text-sm text-muted-foreground">Tell us about your business.</p>
            <p class="text-xs text-muted-foreground">
              Fields marked {@render requiredMark()} are required.
            </p>
          </div>

          <div class="space-y-4">
            <div class="space-y-2">
              <Label for="gs-company">Company name {@render requiredMark()}</Label>
              <Input id="gs-company" bind:value={companyName} autocomplete="organization" aria-invalid={companyInvalid} class="min-h-11" />
            </div>
            <div class="space-y-2">
              <Label for="gs-website">Company website</Label>
              <Input
                id="gs-website"
                type="url"
                inputmode="url"
                bind:value={companyWebsite}
                autocomplete="url"
                placeholder="Example: http://chilla-u-house.com"
                class="min-h-11 placeholder:italic"
                onblur={normalizeWebsite}
              />
            </div>
            <div class="space-y-2">
              <Label for="gs-office-phone">Main office phone</Label>
              <Input
                id="gs-office-phone"
                type="tel"
                inputmode="tel"
                bind:value={mainOfficePhone}
                autocomplete="tel"
                placeholder="Example: (555) 123-4567"
                class="min-h-11 placeholder:italic"
                oninput={onMainOfficePhoneInput}
              />
            </div>
            <div class="space-y-2">
              <Label for="gs-tenant">ServiceTitan Tenant ID {@render requiredMark()}</Label>
              <Input id="gs-tenant" inputmode="numeric" bind:value={tenantId} aria-invalid={tenantInvalid} placeholder="Example: 123456789" class="min-h-11 placeholder:italic" oninput={onTenantInput} />
              <p class="text-xs text-muted-foreground">
                Numbers only. You can get this from your ServiceTitan dashboard, on the left
                under "Integration". Or watch this short YouTube video:
                <a
                  href="https://www.youtube.com/watch?v=urQb1KAlsY8"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium text-primary underline underline-offset-4"
                >How to find your ServiceTitan Tenant ID</a>
              </p>
              <p class="text-xs text-muted-foreground">
                ServiceTitan requires us to know this Tenant ID as a first step to tell them
                you'd like to work with us. Later, in a separate step, you'll give ServiceTitan
                permission to allow us to actually access your data. (We agree with you: it's
                strange that this is multiple different steps. But that's how ServiceTitan chose
                to set things up!)
              </p>
              {#if tenantId && !tenantIdValid}
                <p class="text-xs text-destructive">This should be numbers only.</p>
              {/if}
            </div>

            {#if verticals.length > 0}
              <CreatableMultiSelect
                label="What kind of work do you do?"
                options={verticals}
                placeholder="Search or add your trade…"
                bind:value={selectedVerticals}
              />
            {/if}

            {#if metros.length > 0}
              <CreatableMultiSelect
                label="Your home office is nearest what big city?"
                options={metros}
                placeholder="Search or add your city…"
                bind:value={selectedMetros}
              />
            {/if}
          </div>

          {#if errorMessage}
            <p class="text-sm text-destructive">{errorMessage}</p>
          {/if}

          {#if showStep2Prompt}
            <p class="text-sm text-destructive">Please fill in the highlighted fields.</p>
          {/if}

          {#if hasChoice && !selectedPlanId}
            <p class="text-sm text-muted-foreground">Pick a plan above to keep going.</p>
          {/if}

          <div class="flex flex-col gap-3 sm:flex-row-reverse">
            <Button class="w-full min-h-11 sm:flex-1" disabled={loading} onclick={submitInfo}>
              {loading ? "Please wait…" : "Next: Your Payment"}
            </Button>
            <Button variant="outline" class="w-full min-h-11 sm:flex-1" onclick={backToStep1}>Back</Button>
          </div>
        {:else}
          <div class="space-y-1">
            <h2 class="text-2xl font-semibold">Add your card</h2>
            <p class="text-sm text-muted-foreground">
              Your card is saved securely by Stripe. You won't be charged until your free trial ends.
            </p>
          </div>

          <div class="space-y-2">
            <Label>Card details</Label>
            <div bind:this={cardEl} class="min-h-11 rounded-md border border-border bg-background px-3 py-3"></div>
          </div>

          {#if cardError}
            <p class="text-sm text-destructive">{cardError}</p>
          {/if}

          <Button class="w-full min-h-11" disabled={!cardMounted || loading} onclick={submitCard}>
            {loading ? "Saving…" : "Finish signup"}
          </Button>
        {/if}
      </div>
    {/if}
  </div>
</div>
